import toast from "react-hot-toast";
import { Grey1, trakrefBlue } from "../../color_utils";
import UserInfo from "../../UserInfo";

const renderAnalytics = (
    UserInfo.isSystemAdministrator()
    || (
        !UserInfo.isNativeUser()
        && !UserInfo.isTenantAdministrator()
        && !UserInfo.isTechnicianManager()
    )
);

export const reportOptions = [
    {
        title: "Analytics",
        type: "analytics",
        isManualReport: true,
        render: renderAnalytics,
        reports: [
            { name: "Compliance Checkup", type: "compliance_checkup", id: 1 },
            { name: "Validate Leak Inspections", type: "validate_leak_inspections", id: 2 },
        ],
    },
];

export const listTextStyle = {
    fontSize: '12px',
    color: Grey1
};

export const AllOption = 0;
export const HCFC = 1;
export const CFC = 6;

export const rootBreadCrumbPath = [{ text: "Reports" }];

export const activeReportIndicatorStyle = { borderLeft: `4px solid ${trakrefBlue}`  };

export const skeletonReportItems = ['skeleton1', 'skeleton2'];

export const validateRange = (input, max, min = 0, maxFirstNumber) => {
    const regex = /^(\d+(\.\d+)?)-(\d+(\.\d+)?)$/;
    const match = input.match(regex);
  
    if (!match) return false;
    const firstNumber = parseFloat(match[1]);
    const secondNumber = parseFloat(match[3]);

    return firstNumber >= min && firstNumber <= secondNumber && (max == null || secondNumber <= max) && (maxFirstNumber == null || firstNumber <= maxFirstNumber);
};

export const validateProperty = (array, propertyName, maxValue, minValue = 0) => {
    const propertyObject = array.find((item) => item.Property === propertyName);
  
    if (!propertyObject) {
      return false;
    }
    return validateRange(propertyObject.Value, maxValue, minValue);
  };

export  const validateDate = (input) => {
    const date = input.find((item) => item.Property === "date").Value;
    const [startDateStr, endDateStr] = date.split("-");
    if (!(startDateStr && endDateStr)) {
        return false;
    }
    const startDate = new Date(startDateStr);
    const endDate = new Date(endDateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (startDate > endDate || endDate < startDate) {
      return false;
    }

    if (startDate > today || endDate > today) {
      return false;
    }

    return true;
};

export const getValueOrDefault = (e, data, property, formData) => {
    const {
        target: { value },
    } = e;

    let newValue = value;

    const isResettingToDefault =
        value.length > 1 &&
        !formData[property].some((item) => item.value == -1) &&
        newValue.filter((item) => item.value == -1).length > 0;

    if (value.length === 0 || isResettingToDefault) {
        // Reset to the default value
        newValue = [data[0]];
    } else if (value.length > 1) {
        // Filter out invalid values
        newValue = value.filter((item) => item.value != -1);
    }
    return newValue;
};

export const checkIsDefaultOption = (array = []) => {
    return array.length === 1 && (array[0] == -1 || array[0].value == -1);
};

export const renderWeight = (weightValue) => {
    if (isNaN(weightValue)) return;

    if (UserInfo.weightUnits === 'Lbs') {
        return Number(weightValue.toFixed(2));
    }

    const kg = 0.453592;
    return Number((weightValue * kg).toFixed(2));
};

export const reportErrorMessages = {
    504: 'The report took too long to generate. Narrow the filters and try again.',
    default: 'The report could not be generated. Please try again, and contact support if the problem continues.',
};

// Server text from a JSON-string body (or plain text on a 4xx); '' for empty, ProblemDetails or HTML bodies.
export const getReportErrorMessage = (errorData, statusCode) => {
    const text = (errorData ?? '').trim();
    if (!text) return '';
    try {
        const parsed = JSON.parse(text);
        return typeof parsed === 'string' ? parsed.trim() : '';
    } catch {
        return statusCode < 500 && !text.startsWith('<') ? text : '';
    }
};

export const fetchReportExport = async ({
    url,
    method = 'POST',
    headers = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
    },
    body = {},
    defaultFilename = 'downloaded-file',
    responseType = 'blob',
    onError = (errorData, statusCode) => {
        console.error(`Download error: ${errorData} with status code ${statusCode}`);

        // AB#17145: an empty error body used to toast a blank red X.
        const message = (statusCode ? getReportErrorMessage(errorData, statusCode) : '')
            || reportErrorMessages[statusCode]
            || reportErrorMessages.default;
        return toast.error(message, { duration: 6000 });
    },
    onSuccess = () => toast.success("The report has been successfully generated and is available in your Downloads folder.", { duration: 6000 }),
  }) => {
    const loadingToastId = toast("⏳ The report is being generated.", { duration: 6000 });
    try {
        const response = await fetch(url, {
          method,
          headers: {
            ...headers,
          },
          body: JSON.stringify(body),
          responseType,
        });
        toast.dismiss(loadingToastId);

        if (!response.ok) {
          const errorData = await response.text();
          onError(errorData, response.status);
          return;
        }
    
        let filename = defaultFilename;
        const contentDisposition = response.headers.get('Content-Disposition');
        if (contentDisposition && contentDisposition.includes('filename=')) {
          const matches = contentDisposition.match(/filename[^;=\n]*=(['"]?)(.*?)\1(?:;|$)/);
          if (matches && matches[2]) {
            filename = decodeURIComponent(matches[2]).replaceAll(' ', '');
          }
        }
    
        const blob = await response.blob();
        const urlObject = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = urlObject;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(urlObject);
    
        onSuccess();
      } catch (error) {
        toast.dismiss(loadingToastId);
        onError(error.message);
      }
};

export const getGWP = (value = [], gwp = []) => {
  if (!gwp.length) return 0;
  const refrigerantIds = new Set(value.map(item => parseInt(item.value)));
  if (refrigerantIds.has(-1)) { 
      return Math.max(...gwp.map(refrigerant => refrigerant.gwp));
  }
  let maxGwp = 0;

  for (const refrigerant of gwp) {
      if (refrigerantIds.has(refrigerant.refrigerantTypeId)) {
          maxGwp = Math.max(maxGwp, refrigerant.gwp);
      }
  }

  return maxGwp;
};

export const getMaxAssetCapacity = (currentValue, maxAssetCapacity, type) => {
  const filteredLocations = currentValue && maxAssetCapacity.filter(loc => currentValue.includes(type == "regions" ? loc.region : loc.locationId));
  const maxCapacity = filteredLocations.length > 0 ? Math.max(...filteredLocations.map(loc => loc.maxValue)) : 0; 
  return maxCapacity;
};

export const filterData = (dataList, selectedIds, type) => {
  return dataList.filter(item => selectedIds.includes((type && (type === "regions" ? item.regionId : item.state)) || item.value));
};

export const getLocationFiltersDropdown = (value, type, data, formData, maxChargeCapacity = 0) => {
  const { regions, states, locations, maxAssetCapacity, maxCapacity } = data;
  const { regions: selectedRegions, locations: selectedLocations } = formData;

  let regionFilter = data.filterRegions;
  let stateFilter = data.filterStates;
  let locationFilter = data.filterLocations;
  let newChargeCapacityMax;

  const currentValue = value.map(item => item.value);
  const isDefaultSelection = checkIsDefaultOption(currentValue);
  const isDefaultRegion = checkIsDefaultOption(selectedRegions);
  const currentMaxCapacity = renderWeight(getMaxAssetCapacity(currentValue, maxAssetCapacity, type));

  if (!isDefaultSelection) {
    switch (type) {
      case 'regions':
        locationFilter = locations.filter(f => currentValue.includes(f.regionId));
        stateFilter = filterData(
          states,
          locationFilter.length > 0 ? locationFilter.map(item => item.state) : []
        );
        newChargeCapacityMax = currentMaxCapacity;
        break;

      case 'locations':
        stateFilter = filterData(
          states, 
          value.map(item => item.state), 'states');
        newChargeCapacityMax = Math.max(isDefaultRegion ? maxCapacity : maxChargeCapacity, currentMaxCapacity);
        break;
    }
  } else {
    const locationState = [...new Set(locationFilter.map(item => item.state))];
    switch (type) {
      case 'regions':
        regionFilter = regions;
        locationFilter = locations;
        stateFilter = checkIsDefaultOption(selectedLocations)
          ? states
          : filterData(states, locationState, 'states');
        newChargeCapacityMax = maxCapacity;
        break;

      case 'locations':
        stateFilter = checkIsDefaultOption(selectedRegions)
          ? states
          : filterData(states, locationState);
        newChargeCapacityMax = isDefaultRegion ? maxCapacity : maxChargeCapacity;
        break;
    }
  }

  return {
    regionFilter,
    stateFilter,
    locationFilter,
    newChargeCapacityMax: newChargeCapacityMax
  };
};

export const getUpdatedLocation = (value, type, formData, allDataLocation) => {
  const currentValue = value.map(item => item.value);
  const isDefaultSelection = checkIsDefaultOption(currentValue);
  let updatedLocations = formData.locations;

  switch (type) {
    case 'regions':
      if (isDefaultSelection) {
        updatedLocations = formData.locations;
      } else {
        updatedLocations = formData.locations.filter((item) => currentValue.includes(item.regionId) || item.regionId === -1);
      }
      break;
    case 'locations':
      updatedLocations = value;
      break;
  }
  return {
    locations: updatedLocations.length > 0 ? updatedLocations : [allDataLocation[AllOption]],
  };
};

export const getUpdatedSingleLocation = (value, type, formData, allDataLocation) => {
  const currentValue = value.map(item => item.value);
  const isDefaultSelection = checkIsDefaultOption(currentValue);
  let updatedLocations = formData.locations;

  switch (type) {
    case 'regions':
      if (isDefaultSelection) {
        updatedLocations = [formData.locations];
      } else {
        updatedLocations = [formData.locations].filter((item) => currentValue.includes(item.regionId) || item.regionId === -1);
      }
      break;
    case 'locations':
      updatedLocations = value;
      break;
  }
  return {
    locations: updatedLocations.length > 0 ? updatedLocations : [allDataLocation[AllOption]],
  };
};